import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import { Video, CalendarDays, MessageCircle, ClipboardSignature, PhoneCall, Eye, GripVertical } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function joinDash(to, pathname) {
  if (to.startsWith('/dashboard')) return to;
  if (pathname.startsWith('/dashboard') && to.startsWith('/')) return `/dashboard${to}`;
  return to;
}

const baseSections = [
  { label: 'Start Teleconsultation / परामर्श प्रारंभ', path: '/start-teleconsultation', icon: <Video size={32} />, roles: ['junior resident', 'senior resident', 'faculty'] },
  { label: 'Appointment Schedule / नियुक्तियाँ',       path: '/appointments',           icon: <CalendarDays size={32} />, roles: ['intern', 'junior resident', 'senior resident', 'faculty'] },
  { label: 'Chat History / चैट इतिहास',               path: '/chat-history',           icon: <MessageCircle size={32} />, roles: ['junior resident', 'senior resident', 'faculty'] },
  { label: 'Consent Forms / सहमति पत्र',              path: '/consent-forms',          icon: <ClipboardSignature size={32} />, roles: ['intern', 'junior resident', 'senior resident', 'faculty'] },
  { label: 'Helpline Support / हेल्पलाइन',             path: '/helpline-support',       icon: <PhoneCall size={32} />, roles: ['intern', 'junior resident', 'senior resident', 'faculty', 'citizen'] },
  { label: 'View Citizen Records / अभिलेख देखें',      path: '/view-citizen-records',   icon: <Eye size={32} />, roles: ['citizen'] }
];

function SortableCard({ section, pathname }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.path });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : 'auto',
    opacity: isDragging ? 0.7 : 1
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Link to={joinDash(section.path, pathname)} className="no-underline">
        <Card className="rounded-2xl shadow-md hover:shadow-xl bg-white dark:bg-[#26272b] border border-gray-200 dark:border-[#363640] transition-colors duration-300 ease-in-out cursor-pointer">
          <CardContent className="flex flex-col items-center justify-center py-8 relative">
            <span {...attributes} {...listeners} className="absolute top-2 right-2 cursor-grab active:cursor-grabbing" title="Drag to reorder">
              <GripVertical size={20} className="text-cyan-800 dark:text-cyan-200 opacity-70" />
            </span>
            <div className="mb-3 text-cyan-700 dark:text-cyan-200 transition-colors">{section.icon}</div>
            <Typography variant="h6" align="center" className="text-gray-800 dark:text-gray-100 transition-colors">{section.label}</Typography>
          </CardContent>
        </Card>
      </Link>
    </div>
  );
}

export default function GroupTelemedicine() {
  const user = JSON.parse(localStorage.getItem('loggedInUser') || 'null') || {};
  const role = (user?.role || user?.userType || '').toLowerCase();

  const visible = baseSections.filter(s => s.roles.includes(role));
  const allPaths = visible.map(s => s.path);
  const [order, setOrder] = useState(allPaths);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    async function fetchOrder() {
      try {
        const res = await axios.get(`/api/dashboard-config/telemedicine/${role}`);
        if (res.data && Array.isArray(res.data.order)) {
          const filtered = res.data.order.filter(p => allPaths.includes(p));
          const extra = allPaths.filter(p => !filtered.includes(p));
          setOrder([...filtered, ...extra]);
        } else {
          setOrder(allPaths);
        }
      } catch {
        setOrder(allPaths);
      } finally {
        setLoading(false);
      }
    }
    fetchOrder();
  }, [role]); // no rule reference here

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    const newOrder = arrayMove(order, oldIndex, newIndex);
    setOrder(newOrder);

    // best-effort persist
    axios.post(`/api/dashboard-config/telemedicine/${role}`, { order: newOrder }).catch(() => {});
  };

  const orderedSections = order.map(p => visible.find(s => s.path === p)).filter(Boolean);

  return (
    <div className="p-6 bg-white dark:bg-[#232326] rounded-xl shadow border border-gray-200 dark:border-[#363640] min-h-screen transition-colors">
      <Typography variant="h6" className="text-cyan-800 dark:text-cyan-200 font-semibold mb-4">Telemedicine Services</Typography>

      {loading ? (
        <div className="text-gray-500 dark:text-gray-300 text-center mt-20">Loading...</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={order} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {orderedSections.map((section) => (
                <SortableCard key={section.path} section={section} pathname={location.pathname} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
